/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../api.js";
import googleMaps from "../util/google-maps.js";
import _ from "underscore";
import util from "../util/util.js";
import SortableTableViewModel from "./shared/sortable-table.js";
import jobQueue from "../util/job-queue.js";
import sharedSitesMap from "../map/shared-sites.js";
import regionsMap from "../map/regions.js";
import markerLayer from "../map/marker-layer.js";
import mapStyles from "../map/styles.js";
import h from "../util/dom-builder.js";
import programs from "../programs.js";

var SharedSitesViewModel = function(birdata) {
	document.body.classList.add("birdata-fullscreen");

	var self = this;

	var initialState = util.hashParams();

	self.loaded = ko.observable(false);
	self.sites = ko.observable([]);

	self.map = ko.observable();
	self.mapOptions = ko.observable();

	self.visibleOnMapFilter = ko.observable(!!initialState.visible_on_map).extend({ rateLimit: 100 });
	self.fireAffectedFilter = ko.observable(!!initialState.fire_affected).extend({ rateLimit: 100 });
	self.nameFilter = ko.observable(initialState.name || '').extend({ rateLimit: 400 });
	self.surveyTypeFilter = ko.observable();
	self.stateFilter = ko.observable();
	self.regionFilter = ko.observable();
	self.programFilter = ko.observable();

	self.regionSiteIds = ko.observable();
	self.regionSitesLoading = ko.observable(false);

	self.sortedSites = ko.pureComputed(function() {
		var sites = self.sites();

		sites = _.filter(sites, siteFilterFn());

		if(self.table) {
			_.each(self.table.sortDescriptors(), function(descriptor) {
				if(!descriptor.ascending) sites.reverse();
				var column = self.table.column(descriptor.column);
				sites = _.sortBy(sites, column.params.value);
				if(!descriptor.ascending) sites.reverse();
			});
		}

		return sites;
	});

	// Returns a function that filters sites FAST without evaluating any KO observables
	function siteFilterFn() {
		var filterVisible = self.visibleOnMapFilter();
		var filterFireAffected = self.fireAffectedFilter();
		var bounds = filterVisible ? self.mapBounds() : null; // only evaluate mapBounds if necessary
		var textSearch = self.nameFilter().trim().toLowerCase();
		var surveyType = self.surveyTypeFilter() ? self.surveyTypeFilter().code : null;
		var state = self.stateFilter() ? self.stateFilter().code : null;
		var regionSiteIds = self.regionSiteIds();
		var program = self.programFilter() ? self.programFilter().code : null;

		return function(site) {
			if(filterVisible && bounds && !bounds.contains(new google.maps.LatLng(site.centroid.lat, site.centroid.lon))) return false;
			if(textSearch && site.name.toLowerCase().indexOf(textSearch) == -1) return false;
			if(surveyType && site.survey_type != surveyType) return false;
			if(state && site.state != state) return false;
			if(regionSiteIds && _.indexOf(regionSiteIds, site.id, true) == -1) return false;
			if(program && _.indexOf(site.programs, program) == -1) {
				return false;
			}
			if(filterFireAffected && !(site.metadata && site.metadata.fire_status === 'Burnt')) {
				return false;
			}
			if(site.inactive) {
				return false;
			}
			return true;
		};
	}

	self.renderedSites = ko.observableArray();

	self.mapBounds = ko.observable();

	self.surveyTypes = ko.observable([]);
	self.states = ko.observable([]);
	self.regions = ko.observable([]);
	self.programs = ko.observable([]);

	var safeMe = birdata.me(true).noLoginRedirect().catch(function() {});

	Promise.all([birdata.lookup(), birdata.regions(), safeMe, programs()]).then(function([lookup, regions, me, programLogic]) {
		self.surveyTypes(lookup['survey_type']);
		self.states(lookup['state']);

		self.programs(_.filter(lookup['program'], function(p) {
			var pl = programLogic.find(p.code);

			if(pl && pl.canFilterBy) {
				return pl.canFilterBy(me || {});
			}
			return true;
		}));

		self.regions(regions);

		// Load initial state
		if(initialState.type) {
			self.surveyTypeFilter(_.findWhere(self.surveyTypes(), {code: initialState.type }));
		}
		if(initialState.state) {
			self.stateFilter(_.findWhere(self.states(), {code: initialState.state }));
		}
		if(initialState.region_id) {
			self.regionFilter(_.findWhere(self.regions(), {id: +initialState.region_id }));
		}
		if(initialState.program) {
			self.programFilter(_.findWhere(self.programs(), {code: initialState.program }));
		}
	});

	ko.computed(function() {
		var region = self.regionFilter();
		if(region) {
			self.regionSitesLoading(true);
			birdata.sharedSitesInRegion(region.id).then(function(sites) {
				self.regionSiteIds(_.sortBy(_.pluck(sites, 'id'), _.identity)); // Sort ids for performance later
				self.regionSitesLoading(false);
			});
		} else {
			self.regionSiteIds(null);
			self.regionSitesLoading(false);
		}
	});

	function surveyTypeString(code) {
		if(self.surveyTypes()) {
			var type = _.findWhere(self.surveyTypes(), { code: code });
			return type ? type.name : code;
		} else {
			return code;
		}
	}

	function stateString(code) {
		if(self.states()) {
			var state = _.findWhere(self.states(), {code: code});
			return state ? state.name : code;
		} else {
			return code;
		}
	}

	function getState() {
		var state = {
			visible_on_map: self.visibleOnMapFilter(),
			fire_affected: self.fireAffectedFilter(),
			name: self.nameFilter()
		};
		if(self.map()) {
			var map = self.map();
			self.mapBounds(); // trigger ko binding
			var center = map.getCenter();

			state.map = [center.lat().toFixed(7), center.lng().toFixed(7), map.getZoom()].join('_');
		}
		if(self.surveyTypeFilter()) {
			state.type = self.surveyTypeFilter().code;
		}
		if(self.stateFilter()) {
			state.state = self.stateFilter().code;
		}
		if(self.regionFilter()) {
			state.region_id = self.regionFilter().id;
		}
		if(self.programFilter()) {
			state.program = self.programFilter().code;
		}
		state = _.pick(state, function(v, k) { return !!v; });
		return state;
	}

	ko.computed(function() {
		history.replaceState({}, '', '#' + util.encodeParams(getState()));
	});

	self.selectableSurveyTypes = ko.pureComputed(function() {
		var codes = util.uniq(_.pluck(self.sites(), 'survey_type'));
		return _.filter(self.surveyTypes(), function(type) {
			return codes.indexOf(type.code) != -1;
		});
	});

	self.table = new SortableTableViewModel([
		{
			name: ko.pureComputed(function() { return 'Sites (' + util.formatNumber(self.sortedSites().length) + ')'; }),
			value: function(site) { var name = site.name; return name ? name.trim().toLowerCase() : ""; },
			html: function(site) {
				if(site.name) {
					var result = util.highlightMatches(site.name, self.nameFilter() || "");
					if(site.metadata && site.metadata.fire_status === 'Burnt') {
						result += ' <span class="fire-tag">Fire&nbsp;affected</span>';
					}
					return result;
				} else {
					return "(Unnamed)";
				}
			},
			id: 'site'
		}, {
			name: 'Type',
			value: function(site) { return surveyTypeString(site.survey_type); },
			formatted: function(site) { return this.value(site); },
			id: 'type'
		}, {
			name: 'State',
			value: function(site) { return stateString(site.state); },
			formatted: function(site) { return this.value(site); },
			id: 'state'
		}, {
			name: '',
			id: 'zoom',
			html: function(site) { return "Map";},
			click: function(site, e) {
				e.stopPropagation();
				zoomToSite(site);
				return true;
			}
		}
	]);

	var sitesById = null;
	function siteById(id) {
		if(!sitesById && self.sites().length) {
			sitesById = {};
			self.sites().forEach(site => sitesById[site.id] = site);
		}
		return sitesById[id];
	}

	function zoomToSite(site) {
		if(self.map()) {
			self.map().fitBounds(siteBounds(site));
		}
	}

	function siteBounds(site, bounds) {
		var sw = new google.maps.LatLng(site.bounds.miny, site.bounds.minx);
		var ne = new google.maps.LatLng(site.bounds.maxy, site.bounds.maxx);
		if(bounds) {
			return bounds.extend(sw).extend(ne);
		} else {
			return new google.maps.LatLngBounds(sw, ne);
		}
	}

	function fitSitesOnMap(sites, map) {
		if(sites.length > 0) {
			var bounds = siteBounds(sites[0]);
			_.each(sites, function(site) { bounds = siteBounds(site, bounds); });
			map.fitBounds(bounds);
		}
	}

	self.stateFilter.subscribe(function() {
		if(self.map() && self.states() && !self.visibleOnMapFilter()) {
			setTimeout(function() {
				fitSitesOnMap(self.sortedSites(), self.map());
			}, 100);
		}
	});

	self.regionSiteIds.subscribe(function() {
		if(self.map() && self.regionSiteIds() && !self.visibleOnMapFilter()) {
			setTimeout(function() {
				fitSitesOnMap(self.sortedSites(), self.map());
			}, 100);
		}
	});

	self.clickSite = function(site) {
		window.location = "shared-site-details?id=" + site.id;
	};

	var sharedSitesPromise = birdata.sharedSites();
	var sharedSitesById = {};

	sharedSitesPromise.then(function(sites) {
		self.sites(sites);
		sites.forEach(function(site) {
			sharedSitesById[site.id] = site;
		});
		self.loaded(true);
	});

	ko.computed(function() {
		var map = self.map();
		if(map) setupMap(map);
	});

	function setupMarkerFilter(markerLayer) {
		ko.computed(function() {
			var filterFn = siteFilterFn();
			markerLayer.setFilter(function(marker) {
				var site = marker.markerOptions.userData.sharedSite;
				return filterFn(site);
			});
		});
	}

	function setupMap(map) {
		var hoverOverlay = googleMaps.hoverOverlay(map);

		map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(googleMaps.dragZoomControl(map));

		google.maps.event.addListener(map, 'idle', function() {
			self.mapBounds(map.getBounds());
		});

		sharedSitesPromise.then(function(sharedSites) {
			var layer = markerLayer.layer(map, {
				density: 2,
				maxZoom: 9,
				listeners: {
					click: function() {
						zoomToSite(this.userData.sharedSite);
					}
				}
			});

			var q = jobQueue.queue(500, 10, function(sharedSite) {
				var markerOptions = {
					position: { lat: sharedSite.centroid.lat, lng:sharedSite.centroid.lon },
					title: sharedSiteLabel(sharedSite),
					userData: {
						sharedSite: sharedSite
					},
					icon: mapStyles.sharedSiteIcon(sharedSite),
					zIndex: 150
				};

				layer.addMarkerOptions(markerOptions);
			}, function() {
				layer.refresh();
				setupMarkerFilter(layer);
			});

			_.each(sharedSites, q.add, q);
			q.finish();
		});

		function sharedSiteLabel(site) {
			var title = site.name;
			if(site.metadata && site.metadata.fire_status === 'Burnt') {
				title += ' (Fire affected)';
			}
			return title;
		}

		var sharedSiteLayer = sharedSitesMap.showLayer(map, {
			layerID: "sharedSites",
			polygonEvents: {
				mousemove: function(evt) {
					hoverOverlay.position(evt.latLng);
				},
				mouseover: function(evt) {
					var site = siteById(this.userData.id);
					if(site) {
						hoverOverlay.html(sharedSiteLabel(site));
						hoverOverlay.position(evt.latLng);
						hoverOverlay.show();
					}
				},
				mouseout: function(evt) {
					hoverOverlay.hide();
				},
				click: function(evt) {
					var site = siteById(this.userData.id);
					if(site) {
						self.clickSite(site);
					}
				}
			},
			markerEvents: {
				mousemove: function(evt) {
					hoverOverlay.position(evt.latLng);
				},
				mouseover: function(evt) {
					var hoverContent = this.userData.map(x => siteById(x.id)).filter(x => x).map(sharedSiteLabel).join(", ");
					hoverOverlay.html(hoverContent);
					hoverOverlay.position(evt.latLng);
					hoverOverlay.show();
				},
				mouseout: function(evt) {
					hoverOverlay.hide();
				},
				click: function(evt) {
					if(this.userData.length == 1) {
						self.clickSite(this.userData[0]);
					} else if(this.userData.length > 1) {
						var content = h('ul',
							this.userData.map(x => siteById(x.id)).filter(x => x).map(site =>
								h('li', { $click: evt => self.clickSite(site), 'class': 'link' }, site.name)))
						var infoWindow = new google.maps.InfoWindow({
							content: content
						});
						infoWindow.open(self.map(), this);
					}
				}
			},
			sharedSiteLookup: function(id) {
				return sharedSitesById[id];
			}
		});

		ko.computed(function() {
			var ids = _.pluck(self.sortedSites(), 'id');

			sharedSiteLayer.setFeatureFilter(function(feature) {
				try {
					var siteId = feature.userData.attributes.id;
					return ids.indexOf(siteId) != -1;
				} catch(e) {
					return false;
				}
			});
		});

		// Region layer

		var regionLayer = regionsMap.showLayer(map);

		ko.computed(function() {
			var region = self.regionFilter();

			regionLayer.setFeatureFilter(function(feature) {
				return region && feature.userData.attributes.id == region.id;
			});
		});

	}

	googleMaps.load().then(function() {
		var center = new google.maps.LatLng(-22.50831, 136.078612);
		var zoom = 4;

		if(initialState.map) {
			var x = initialState.map.split("_");
			center = new google.maps.LatLng(+x[0], +x[1]);
			zoom = +x[2];
		}

		self.mapOptions(googleMaps.defaultOptions({
			center: center,
			zoom: zoom,
			fullscreenControl: false
		}));
	});
};

ko.components.register('shared-sites', {
	viewModel: function() {
		return new SharedSitesViewModel(birdata);
	},
	template: require('../../templates/shared-sites.html')
});

export default SharedSitesViewModel;
